
import React from 'react';
import { useBreakpoint } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Clock, Users, TrendingDown, Zap, CheckCircle, XCircle } from 'lucide-react';

const CostAnalysisSection = () => {
  const { isMobile } = useBreakpoint();

  const costItems = [
    {
      icon: Clock,
      text: "Hours wasted crafting cold emails that get deleted"
    },
    {
      icon: Users,
      text: "Networking events you've avoided (and the potential clients you never met)"
    },
    {
      icon: TrendingDown,
      text: "The confidence hit every time you get rejected or ignored"
    },
    {
      icon: Zap,
      text: "The stress of never knowing where your next client will come from"
    },
    {
      icon: Clock,
      text: "The feast-or-famine cycles that keep you up at night"
    }
  ];

  const noMoreItems = [
    "No pushy sales tactics...",
    "No uncomfortable conversations…",
    "No rejection!"
  ];

  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/3 left-1/4 w-64 h-64 bg-red-100/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-[#FEE6D8]/20 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 md:px-8 max-w-4xl">
        <div className="space-y-12">
          
          {/* Cost Analysis Header */}
          <div className="text-center">
            <h2 className={cn(
              "font-bold text-gray-900 leading-tight mb-6",
              isMobile ? "text-2xl" : "text-3xl lg:text-4xl"
            )}>
              Think about what this is <span className="text-red-600">costing you</span>:
            </h2>
          </div>

          {/* Cost Items List */}
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl border border-red-100">
            <div className="space-y-6">
              {costItems.map((item, index) => (
                <div key={index} className="flex items-start gap-4 group">
                  <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center group-hover:bg-red-200 transition-colors">
                    <item.icon className="text-red-600" size={20} />
                  </div>
                  <p className={cn(
                    "text-gray-800 leading-relaxed flex-grow",
                    isMobile ? "text-base" : "text-lg"
                  )}>
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Good News Section */}
          <div className="bg-gradient-to-r from-[#003a70]/5 to-[#FEE6D8]/10 rounded-2xl p-6 md:p-8 border border-[#FEE6D8]/50 shadow-lg">
            <div className="space-y-4">
              <p className={cn(
                "text-gray-800 leading-relaxed",
                isMobile ? "text-lg" : "text-xl"
              )}>
                <strong className="text-[#003a70]">The good news?</strong> There's a simple solution that flips this entire dynamic on its head. Instead of you chasing prospects, they start chasing you.
              </p>
              
              <p className={cn(
                "text-gray-800 leading-relaxed",
                isMobile ? "text-lg" : "text-xl"
              )}>
                That's exactly what <strong className="text-[#003a70]">The Genius Card Playbook</strong> teaches you - how to use AI-powered business cards as your secret weapon for generating monthly recurring revenue, simply by demonstrating something so cool that prospects ask <em className="text-[#003a70] font-semibold">YOU</em> for the sale.
              </p>
            </div>
          </div>

          {/* Transformation Section */}
          <div className="text-center space-y-8">
            <h3 className={cn(
              "font-bold text-[#003a70] leading-tight",
              isMobile ? "text-2xl" : "text-3xl lg:text-4xl"
            )}>
              Imagine This Transformation...
            </h3>

            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl border border-[#FEE6D8]/50">
              <p className={cn(
                "text-gray-800 leading-relaxed mb-6",
                isMobile ? "text-lg" : "text-xl"
              )}>
                Imagine walking into your next networking event actually <strong className="text-green-600">EXCITED</strong>. Instead of dreading conversations, you're looking forward to showing off your AI business card.
              </p>
              
              <p className={cn(
                "text-gray-800 leading-relaxed",
                isMobile ? "text-lg" : "text-xl"
              )}>
                When someone asks what you do, you simply pull out your phone, show them your card in action, and watch their eyes light up as they immediately ask how they can get one for their business.
              </p>
            </div>

            {/* No More List */}
            <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-6 md:p-8 border border-red-200 shadow-lg">
              <div className="space-y-4">
                {noMoreItems.map((item, index) => (
                  <div key={index} className="flex items-center justify-center gap-3">
                    <XCircle className="text-red-500 flex-shrink-0" size={24} />
                    <span className={cn(
                      "text-gray-800 font-medium",
                      isMobile ? "text-lg" : "text-xl"
                    )}>
                      {item}
                    </span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 pt-6 border-t border-red-200">
                <p className={cn(
                  "text-[#003a70] font-bold text-center",
                  isMobile ? "text-lg" : "text-xl"
                )}>
                  Just demonstrate, and let them ask YOU for the sale.
                </p>
              </div>
            </div>

            {/* Final Explanation */}
            <div className="bg-[#FEE6D8]/30 rounded-2xl p-6 md:p-8 border border-[#FEE6D8] shadow-lg">
              <p className={cn(
                "text-gray-800 leading-relaxed",
                isMobile ? "text-lg" : "text-xl"
              )}>
                This works because you're not trying to sell them anything. You're simply showing them something innovative that solves a real problem they have. <strong className="text-[#003a70]">The technology does all the heavy lifting.</strong>
              </p>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default CostAnalysisSection;
